import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'components/base';
import useDataContext from 'data/context/data';

import { isPriced, lengthLabel, railPriceFrom } from '../../lib/displayPrices.mjs';
import { money, productPrice } from '../../lib/landingPrice';
import { ProductDetailLink } from '../pageLinks';
import PdpIcon from '../product/pdpIcon';
import { Picture, Responsive, SectionHead, imageShape } from '../landing/shared';

// Plain list prices: the claimed coupon shows on the accessory cards below, not on the chips.
// Null when nothing fitting is for sale, the chip is then left out.
const chipPrice = (product, lengths) => {
  if (!isPriced(product)) return null;
  if (!lengths) {
    const { price, priceFrom } = productPrice(product);
    return { desktop: `${priceFrom ? 'from ' : ''}${price}` };
  }
  const from = railPriceFrom(product.options, lengths);
  if (from == null) return null;
  const phone = `from ${money(from, product.currency)}`;
  return { desktop: `${lengthLabel(lengths)} · ${phone}`, phone };
};

const FitChip = ({ product, price, onClick }) => (
  <ProductDetailLink productId={product.id}>
    <a className="dsp-chip" onClick={onClick}>
      <span className="dsp-chip-label">{product.name}</span>
      <span className="dsp-chip-price">
        <Responsive desktop={price.desktop} phone={price.phone} />
      </span>
      <PdpIcon name="chevronRight" size={16} className="lnd-phone dsp-chip-chevron" />
    </a>
  </ProductDetailLink>
);

FitChip.propTypes = {
  product: PropTypes.object.isRequired,
  price: PropTypes.shape({ desktop: PropTypes.string.isRequired, phone: PropTypes.string })
    .isRequired,
  onClick: PropTypes.func,
};

// One row per print the customer already has, each listing the accessories cut for it. The
// print name links to its PDP.
const FitList = ({ id, rows, track }) => {
  const dataContext = useDataContext();

  return (
    <section id={id} className="lnd-section dsp-fit">
      <Container>
        <SectionHead
          eyebrow="Find your fit"
          title="Start with the print you have"
          aside={
            <Responsive
              desktop="Every accessory is cut for a Parabo print size. Find your print, then pick how you want to show it."
              phone="Find your print, then pick how to show it."
            />
          }
        />
        <ul className="dsp-fit-list">
          {rows.map((row) => {
            const print = row.printId && dataContext.getProduct(row.printId);
            const chips = row.accessories
              .map(({ id: productId, lengths }) => {
                const product = dataContext.getProduct(productId);
                return { product, price: product && chipPrice(product, lengths) };
              })
              .filter(({ price }) => price);
            // A print with nothing to show it in is left out.
            if (!chips.length) return null;

            return (
              <li key={row.name} className="dsp-fit-row">
                <div className="dsp-fit-print">
                  <span
                    className={row.icon ? 'dsp-fit-thumb dsp-fit-thumb--icon' : 'dsp-fit-thumb'}
                  >
                    {row.icon ? (
                      <PdpIcon name={row.icon} size={28} />
                    ) : (
                      <Picture image={row.image} sizes="80px" alt="" />
                    )}
                  </span>
                  <div>
                    <h3 className="dsp-fit-name">
                      {print ? (
                        <ProductDetailLink productId={print.id}>
                          <a onClick={track('print', 'fit', print.id)}>
                            {row.name}
                            <PdpIcon name="chevronRight" size={18} />
                          </a>
                        </ProductDetailLink>
                      ) : (
                        row.name
                      )}
                    </h3>
                    {row.sizes.map((line) => (
                      <p key={line} className="dsp-fit-sizes">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
                <PdpIcon name="arrowRight" size={22} className="dsp-fit-arrow" />
                <ul className="dsp-fit-chips">
                  {chips.map(({ product, price }) => (
                    <li key={product.id}>
                      <FitChip
                        product={product}
                        price={price}
                        onClick={track('accessory', 'fit', product.id)}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </Container>
    </section>
  );
};

FitList.propTypes = {
  id: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      sizes: PropTypes.arrayOf(PropTypes.string).isRequired,
      printId: PropTypes.string,
      image: imageShape,
      icon: PropTypes.string,
      accessories: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          lengths: PropTypes.arrayOf(PropTypes.number),
        }),
      ).isRequired,
    }),
  ).isRequired,
  track: PropTypes.func.isRequired,
};

export default FitList;
